import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import _, { get } from "lodash";
import { Merchant, Data } from "../../../store/merchant/types";
import { saveRequest as saveMerchant } from "../../../store/merchant/actions";
import { Formik, Form, FormikProps } from "formik";
import schema from "./merchantEditForm.schema";
import pageStyles from "../../pageStyles";
import ServerAlert from "../../../common/errors/ServerAlert";
import { deepTrim } from "../../../lib/utils";
import FormSubmitButtons from "../../../common/buttons/FormSubmitButtons";
import ScrollSpyTabs, { ItemTab } from "../../../common/nav/ScrollSpyTabs";
import SectionWrapper from "../../../common/pageSections/SectionWrapper";
import EditMerchantInformationComponent from "./EditMerchantInformationComponent";
import EditMerchantProcessorComponent from "./EditMerchantProcessorComponent";
import EditMerchantContactComponent from "./EditMerchantContactComponent";
import EditMerchantSurchargeComponent from "./EditMerchantSurchargeComponent";
import MerchantConfigurationForm from "./MerchantConfigurationForm";
import { Paper } from "@mui/material";
import useUserAccess from "../../../effects/useUserAccess";
import { useToastMessage } from "../../../effects/useToastMessage";
import SectionReadForbidden from "../../../common/sectionAccess/SectionReadForbidden";
import { toast } from "react-toastify";

interface Props {
  merchant?: Data;
  isCreate: boolean;
}

function cleanDataBeforeSave(values: Data): Data {
  return values;
}

function prepareDataBeforeLoad(values: Data): Data {
  return values;
}

export default function MerchantEditForm({ merchant, isCreate }: Props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();
  const access = useUserAccess();

  const SectionAccess = get(access, "Merchant.SectionAccess", {});
  const MerchantInfoAccess = get(SectionAccess, "MerchantInfo.Access", {
    R: false,
    U: false,
  });
  const ProcessorAccess = get(SectionAccess, "Processor.Access", {
    R: false,
    U: false,
  });
  const ContactAccess = get(SectionAccess, "Contact.Access", {
    R: false,
    U: false,
  });
  const SurchargeAccess = get(SectionAccess, "Surcharge.Access", {
    R: false,
    U: false,
  });
  const ConfigurationAccess = get(SectionAccess, "Configuration.Access", {
    R: false,
    U: false,
  });

  let initialValues = merchant || {
    id: undefined,
    type: "Merchant",
    attributes: {
      Name: "",
      Address1: "",
      Address2: "",
      Suburb: "",
      State: "",
      Postcode: "",
      MCC: "",
      MID: "",
      Note: "",
      Processor: [],
      Contact: [],
      SurchargePercentage: [],
    },
  };
  initialValues = prepareDataBeforeLoad(initialValues);

  return (
    <Formik
      // enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values, actions) => {
        let valuesToUpdate = _.cloneDeep(values);
        valuesToUpdate = deepTrim(valuesToUpdate);
        valuesToUpdate = cleanDataBeforeSave(valuesToUpdate);
        const callback = (err: string | null, data: Merchant | null) => {
          if (err) {
            let errText = `An error occurred while saving: ${err}`;
            console.log(err);
            toast.error(errText, {
              closeOnClick: true,
            });
          } else {
            if (isCreate) {
              toast.success("Created successfully!", {
                closeOnClick: true,
              });
              history.goBack();
            } else {
              toast.success("Saved successfully!", {
                closeOnClick: true,
              });
            }
          }
          actions.setSubmitting(false);
        };
        dispatch(saveMerchant(valuesToUpdate, callback));
      }}
    >
      {(props: FormikProps<Data>) => {
        const { isSubmitting, errors } = props;

        // Show toast errors
        useToastMessage(isSubmitting, errors);

        const merchantEditTabsContent: ItemTab[] = [
          {
            label: "Merchant Info",
            component: (
              <SectionWrapper title="Merchant Info">
                {MerchantInfoAccess.R ? (
                  <EditMerchantInformationComponent
                    isUpdateAllowed={MerchantInfoAccess.U}
                    merchant={merchant}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Processor",
            component: (
              <SectionWrapper title="Processor">
                {ProcessorAccess.R ? (
                  <EditMerchantProcessorComponent
                    isUpdateAllowed={ProcessorAccess.U}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Contact",
            component: (
              <SectionWrapper title="Contact">
                {ContactAccess.R ? (
                  <EditMerchantContactComponent
                    isUpdateAllowed={ContactAccess.U}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Surcharge",
            component: (
              <SectionWrapper title="Surcharge">
                {SurchargeAccess.R ? (
                  <EditMerchantSurchargeComponent
                    isUpdateAllowed={SurchargeAccess.U}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
          {
            label: "Settlements",
            component: (
              <SectionWrapper title="Settlements">
                {ConfigurationAccess.R ? (
                  <MerchantConfigurationForm
                    isUpdateAllowed={ConfigurationAccess.U}
                  />
                ) : (
                  <SectionReadForbidden />
                )}
              </SectionWrapper>
            ),
          },
        ];

        return (
          <Form className={classes.formWrapper}>
            <Paper className={classes.paper}>
              <ScrollSpyTabs tabsInScroll={merchantEditTabsContent} />
            </Paper>
            <FormSubmitButtons isDisabled={isSubmitting} />
          </Form>
        );
      }}
    </Formik>
  );
}
